import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, WarningCircle, FileText, DownloadSimple, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DrawingShare() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [approved, setApproved] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });

  useEffect(() => {
    api.get(`/public/drawings/${token}`)
      .then((r) => { setMeta(r.data); if (r.data.customer_approved_at) setApproved(r.data.customer_approved_at); })
      .catch((err) => setError(apiError(err.response?.data?.detail) || "This link is invalid or has expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const openFile = async () => {
    setBusy(true);
    const w = window.open("about:blank", "_blank");
    try {
      const { data } = await api.get(`/public/drawings/${token}/file`);
      if (w && !w.closed) w.location.href = data.data; else window.open(data.data, "_blank");
    } catch (err) {
      if (w && !w.closed) w.close();
      toast.error(apiError(err.response?.data?.detail) || "Could not open this file");
    } finally { setBusy(false); }
  };

  const downloadFile = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/public/drawings/${token}/file`);
      const el = document.createElement("a");
      el.href = data.data; el.download = data.name || meta?.file_name || "drawing"; el.click();
    } catch { toast.error("Could not download this file"); } finally { setBusy(false); }
  };

  const subscribe = async () => {
    if (!form.email.trim()) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      await api.post(`/public/drawings/${token}/subscribe`, form);
      setSubscribed(true);
      toast.success("You'll be emailed whenever this drawing is updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not subscribe"); } finally { setBusy(false); }
  };

  const approve = async () => {
    if (!form.name.trim()) { toast.error("Enter your name to approve"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/drawings/${token}/approve`, { name: form.name });
      setApproved(data.approved_at);
      toast.success("Drawing approved — thank you");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not approve"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-6" data-testid="drawing-share-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <img src="/dvepl-logo.svg" alt="DVEPL" className="w-9 h-9 rounded-md" />
          <span className="font-heading font-black text-lg text-zinc-50">DVEPL</span>
        </div>

        {loading && <div className="text-center text-sm text-zinc-500 font-mono" data-testid="drawing-share-loading">Loading drawing…</div>}

        {!loading && error && (
          <div className="border border-red-500/30 bg-red-500/5 rounded-sm p-8 text-center" data-testid="drawing-share-error">
            <WarningCircle size={32} className="text-red-400 mx-auto mb-3" weight="fill" />
            <p className="text-sm text-zinc-300">{error}</p>
          </div>
        )}

        {!loading && !error && meta && (
          <div className="border border-zinc-800 bg-zinc-900/50 rounded-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2.5">
              <FileText size={20} className="text-emerald-600" weight="bold" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-600 font-bold">
                  {meta.category}{meta.panel_name ? ` · ${meta.panel_name}` : ""}
                </div>
                <div className="font-heading text-lg font-black text-zinc-50 tracking-tight" data-testid="drawing-share-name">
                  {meta.file_name}
                </div>
              </div>
            </div>

            <div className="px-5 py-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-zinc-400">
                <span>Order</span>
                <span className="text-zinc-100">{meta.order_no} — {meta.order_name}</span>
              </div>
              <div className="flex justify-between text-zinc-400">
                <span>Uploaded</span>
                <span className="text-zinc-100 font-mono">{(meta.uploaded_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            </div>

            <div className="px-5 pb-4 grid grid-cols-2 gap-2">
              <Button onClick={openFile} disabled={busy} data-testid="drawing-share-view"
                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm gap-1.5 text-sm">
                <FileText size={15} /> View
              </Button>
              <Button onClick={downloadFile} disabled={busy} data-testid="drawing-share-download"
                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm gap-1.5 text-sm">
                <DownloadSimple size={15} /> Download
              </Button>
            </div>

            <div className="px-5 py-4 border-t border-zinc-800 space-y-3">
              {approved ? (
                <div className="text-center" data-testid="drawing-share-approved">
                  <CheckCircle size={28} className="text-emerald-400 mx-auto mb-2" weight="fill" />
                  <p className="text-sm text-zinc-200 font-semibold">Approved</p>
                  <p className="text-xs text-zinc-500 mt-1">Recorded on {String(approved).slice(0, 16).replace("T", " ")}. Our team has been notified.</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Your name</label>
                    <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your name" className="bg-zinc-950 border-zinc-800 text-zinc-50 rounded-sm" data-testid="drawing-share-approve-name" />
                  </div>
                  <Button onClick={approve} disabled={busy} data-testid="drawing-share-approve-btn"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold h-10 gap-1.5">
                    <CheckCircle size={16} weight="bold" /> Approve this Drawing
                  </Button>
                </>
              )}

              <div className="pt-3 border-t border-zinc-800/60">
                {subscribed ? (
                  <p className="text-xs text-emerald-400 text-center" data-testid="drawing-share-subscribed">You'll be emailed if this drawing changes.</p>
                ) : (
                  <>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1.5">Alert me on changes</label>
                    <div className="flex gap-2">
                      <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="you@example.com" className="bg-zinc-950 border-zinc-800 text-zinc-50 rounded-sm text-sm" data-testid="drawing-share-subscribe-email" />
                      <Button onClick={subscribe} disabled={busy} variant="outline" className="border-zinc-800 text-zinc-200 rounded-sm shrink-0 gap-1" data-testid="drawing-share-subscribe-btn">
                        <BellRinging size={14} /> Subscribe
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
